import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ArrowLeftRight } from "lucide-react";

interface Version {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  version: number;
  created_at: string;
}

interface AttachmentComparisonProps {
  version1: Version;
  version2: Version;
}

export const AttachmentComparison = ({ version1, version2 }: AttachmentComparisonProps) => {
  const [url1, setUrl1] = useState<string | null>(null);
  const [url2, setUrl2] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFiles();
    return () => {
      if (url1) URL.revokeObjectURL(url1);
      if (url2) URL.revokeObjectURL(url2);
    };
  }, [version1, version2]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const [result1, result2] = await Promise.all([
        supabase.storage.from("claim-attachments").download(version1.file_path),
        supabase.storage.from("claim-attachments").download(version2.file_path)
      ]);

      if (result1.error) throw result1.error;
      if (result2.error) throw result2.error;

      const newUrl1 = URL.createObjectURL(result1.data);
      const newUrl2 = URL.createObjectURL(result2.data);

      setUrl1(newUrl1);
      setUrl2(newUrl2);
    } catch (error: any) {
      toast.error("Failed to load files for comparison");
      console.error("Error loading files:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderFile = (url: string | null, version: Version) => {
    if (!url) return null;

    const fileType = version.file_type.toLowerCase();

    if (fileType.includes('image')) {
      return (
        <img
          src={url}
          alt={`Version ${version.version}`}
          className="w-full h-auto rounded-lg border"
        />
      );
    } else if (fileType.includes('pdf')) {
      return (
        <iframe
          src={url}
          className="w-full h-[60vh] rounded-lg border"
          title={`Version ${version.version}`}
        />
      );
    } else {
      return (
        <div className="flex items-center justify-center h-[60vh] border rounded-lg bg-muted">
          <p className="text-muted-foreground">Preview not available for this file type</p>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center gap-2 text-muted-foreground">
        <span className="text-sm font-medium">Version {version1.version}</span>
        <ArrowLeftRight className="h-4 w-4" />
        <span className="text-sm font-medium">Version {version2.version}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Version {version1.version}
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {new Date(version1.created_at).toLocaleDateString()}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderFile(url1, version1)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Version {version2.version}
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {new Date(version2.created_at).toLocaleDateString()}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderFile(url2, version2)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
